/* oxlint-disable grafit-oxlint/no-tailwind-font-classes -- Typography 2 migration */
import { Trans } from "@grafit/i18n";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { useRef } from "react";
import { useOverflow } from "../../hooks/use-overflow";
import { cn } from "../../utils/cn";
import { TextTooltip } from "./tooltip";

const labelVariants = cva("font-medium leading-none peer-disabled:cursor-not-allowed", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
    },
  },
  defaultVariants: { size: "md" },
});

export type LabelProps = React.ComponentProps<typeof LabelPrimitive.Root> &
  VariantProps<typeof labelVariants> & {
    required?: boolean;
    subText?: string;
    truncate?: boolean;
  };

const Label = ({
  className,
  children,
  required = false,
  subText,
  truncate: shouldTruncate,
  size,
  ...props
}: LabelProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const isOverflowing = useOverflow(textRef);

  const labelContent = (
    <LabelPrimitive.Root className={cn(labelVariants({ size }), shouldTruncate && "min-w-0", className)} {...props}>
      <span className={cn("inline-flex w-full flex-col", shouldTruncate && "max-w-full overflow-hidden")}>
        <span className={cn("inline-flex w-full items-start gap-1", shouldTruncate && "max-w-full overflow-hidden")}>
          <span
            ref={shouldTruncate ? textRef : undefined}
            className={cn({ truncate: shouldTruncate, "w-full": !required })}
          >
            {children}
          </span>

          {required && (
            <span className="font-semibold text-red-500 text-sm">
              <Trans i18nKey="global.required" />
            </span>
          )}
        </span>
        {subText && <span className="font-normal text-neutral-500 text-xs opacity-60">{subText}</span>}
      </span>
    </LabelPrimitive.Root>
  );

  if (shouldTruncate && isOverflowing) {
    return (
      <TextTooltip text={textRef.current?.textContent ?? ""} side="top" asChild>
        {labelContent}
      </TextTooltip>
    );
  }

  return labelContent;
};

const FloatingLabel = ({ className, children, ...props }: Omit<React.ComponentProps<typeof Label>, "subText">) => (
  <Label
    className={cn(
      "absolute start-1 top-0 z-10 block -translate-y-1/2 rounded-lg bg-neutral-0 px-2 font-medium text-neutral-900 text-xs",
      className,
    )}
    {...props}
  >
    {children}
  </Label>
);

export { FloatingLabel, Label };
